import React, { useState, useEffect } from "react";
import {
  NumberInput,
  Button,
  Group,
  Paper,
  TextInput,
  Grid,
  Text,
  Modal,
  Stack,
} from "@mantine/core";
import { observer } from "mobx-react";
import { Cubby } from "../stores/Cubby";

const SendFundsAdvanced = observer(({ cubby }: { cubby: Cubby }) => {
  const [satoshis, setSatoshis] = useState(0);
  const [bitcoinAddress, setBitcoinAddress] = useState("");
  const [bitcoinAddressError, setBitcoinAddressError] = useState("");
  const [modalOpened, setModalOpened] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [satsPerVirtualByte, setsatsPerVirtualByte] = useState(20); // New state variable for Sats Per v/Byte

  useEffect(() => {
    cubby.cubbyFastInfo("does_need_2FA_form");
  }, []); // Run only once when the component mounts

  const handleSatoshisChange = (newValue: number) => {
    setSatoshis(newValue);
  };

  const handleBitcoinAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newAddress = event.currentTarget.value;
    setBitcoinAddress(newAddress);
  };

  const handlesatsPerVirtualByteChange = (newValue: number) => {
    setsatsPerVirtualByte(newValue); // Update Sats Per v/Byte
  };

  const handleOpenModal = () => {
    setModalOpened(true);
  };

  const handleConfirmSend = () => {
    // Sending of funds with Sats Per v/Byte added
    console.log(
      `Sending ${satoshis} satoshis to ${bitcoinAddress} with 2FA code: ${twoFACode} and ${satsPerVirtualByte} sats per v/byte`
    );
    const data = {
      amountInSatoshis: satoshis,
      destinationAddress: bitcoinAddress,
      twoFactorCode: twoFACode,
      satsPerVirtualByte: satsPerVirtualByte, // Include satsPerVirtualByte in the data
    };
    cubby.cubbyLife("dangerous_send_payment_advanced", data);
    cubby.setModal(null);
  };

  return (
    <Paper p="md" shadow="sm">
      <Stack>
        <Text c="red">
          Advanced mode. You must set the transaction fee. Be careful!
        </Text>
        <Text>
          {" "}
          current balance: {cubby.safeChainBalance.toLocaleString()} satoshis
        </Text>
      </Stack>
      <>
        <form>
          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <NumberInput
                label="Satoshis"
                description="Enter the amount of satoshis to send"
                value={satoshis}
                onChange={handleSatoshisChange}
                min={1}
                required
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput
                label="Bitcoin Address"
                description="Enter the Bitcoin address to send funds to"
                value={bitcoinAddress}
                onChange={handleBitcoinAddressChange}
                required
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <NumberInput
                label="Sats Per v/Byte"
                description="Enter the sats per v/byte for the transaction"
                value={satsPerVirtualByte}
                onChange={handlesatsPerVirtualByteChange}
                min={1}
                required
              />
            </Grid.Col>
          </Grid>
          <Group justify="space-between" mt="md">
            <Button onClick={handleOpenModal}>Send</Button>
          </Group>
        </form>
      </>
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="Confirm Transaction"
        centered
      >
        <Stack>
          <Text>
            You are going to send {satoshis.toLocaleString()} satoshis (
            {satoshis / 100000000} BTC) to this address:
          </Text>

          <Text size="xs">{bitcoinAddress}</Text>
          <Text>
            {" "}
            You will be paying {satsPerVirtualByte} satoshis per vbyte in fees.{" "}
          </Text>
          <Text>
            You are in advanced mode, so we aren't sanity-checking any of these
            values.
          </Text>
          <Text>Be careful: This action can't be reversed!</Text>
        </Stack>

        {cubby.shouldShow2FAFormForPayments && (
          <>
            <TextInput
              label="Enter 2FA Code"
              maxLength={7}
              value={twoFACode}
              onChange={(event) => setTwoFACode(event.currentTarget.value)}
              mt="md"
            />
            <Text size="xs" c="dimmed">
              Get the code from your authenticator app
            </Text>
          </>
        )}
        <Group justify="flex-end" mt="lg">
          <Button variant="outline" onClick={() => setModalOpened(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              cubby.shouldShow2FAFormForPayments && twoFACode.length < 6
            }
            color="red"
            onClick={handleConfirmSend}
          >
            Submit
          </Button>
        </Group>
      </Modal>
    </Paper>
  );
});

export default SendFundsAdvanced;
